/**
 * The runs this browser still holds, as a list a person can act on.
 *
 * Pure: the caller reads the three tables, this decides what they mean
 * together. Everything shown is something the app recorded — no run is inferred
 * and no count is estimated.
 *
 * Three keys are in play and they are NOT interchangeable, which is the whole
 * reason this assembly lives in one tested place:
 *
 * - `analysisKey` identifies the run. It is a hash of the inputs and the
 *   policy, and it is what the cache, the export receipts and — since schema
 *   v9 — the decision log all use. Before v9 the decisions table's
 *   `analysisKey` column actually held `review.generatedAt`; the upgrade
 *   rewrites what it can attribute, and a row it could not is a run this cache
 *   no longer holds.
 * - `createdAt` is when the row reached the cache, which is not when the run
 *   happened.
 *
 * Conflating the two would attribute one run's date to another.
 */

export type HistoryAnalysis = {
  analysisKey: string;
  createdAt: string;
  result: {
    metadata: {
      baselineFileName: string;
      latestFileName: string;
      generatedAt: string;
    };
    qualityIssues: unknown[];
    summary: { qualityGate: "Pass" | "Warning" | "Quarantined" };
  };
  review?: { profileId: string; profileVersion: number; generatedAt: string } | null;
};

/** Decision rows, scoped by the run's analysis key. */
export type HistoryDecision = { analysisKey: string };

/** Export receipts, scoped by the real analysis key. */
export type HistoryExport = { analysisKey: string };

export type RunHistoryRow = {
  analysisKey: string;
  /** When the run happened, from the review; else when it was cached. */
  ranAt: string;
  /** True when `ranAt` is the cache timestamp rather than the run's own. */
  ranAtIsCacheTime: boolean;
  baselineFileName: string;
  latestFileName: string;
  /** Null for a run cached before a profile was always required. */
  profileId: string | null;
  profileVersion: number | null;
  verdict: "Pass" | "Warning" | "Quarantined";
  findingCount: number;
  decisionCount: number;
  exported: boolean;
};

export function buildRunHistory(input: {
  analyses: HistoryAnalysis[];
  decisions?: HistoryDecision[];
  exports?: HistoryExport[];
}): RunHistoryRow[] {
  const decisionsByScope = new Map<string, number>();
  for (const row of input.decisions ?? []) {
    decisionsByScope.set(row.analysisKey, (decisionsByScope.get(row.analysisKey) ?? 0) + 1);
  }
  const exported = new Set((input.exports ?? []).map((row) => row.analysisKey));

  return input.analyses
    .map((entry) => {
      const review = entry.review ?? null;
      const ranAt = review?.generatedAt ?? entry.createdAt;
      return {
        analysisKey: entry.analysisKey,
        ranAt,
        ranAtIsCacheTime: review?.generatedAt === undefined,
        baselineFileName: entry.result.metadata.baselineFileName,
        latestFileName: entry.result.metadata.latestFileName,
        profileId: review?.profileId ?? null,
        profileVersion: review?.profileVersion ?? null,
        verdict: entry.result.summary.qualityGate,
        findingCount: entry.result.qualityIssues.length,
        // Same key as the run itself now, so this is a direct join rather than
        // a lookup through a second identifier.
        decisionCount: decisionsByScope.get(entry.analysisKey) ?? 0,
        exported: exported.has(entry.analysisKey)
      };
    })
    .sort((left, right) => right.ranAt.localeCompare(left.ranAt));
}

/** Counts for the page header. */
export function summarizeHistory(rows: RunHistoryRow[]): {
  total: number;
  quarantined: number;
  withDecisions: number;
  exported: number;
} {
  return {
    total: rows.length,
    quarantined: rows.filter((row) => row.verdict === "Quarantined").length,
    withDecisions: rows.filter((row) => row.decisionCount > 0).length,
    exported: rows.filter((row) => row.exported).length
  };
}
